import { useState } from "react";
import { Icon } from "../components/Icon";
import { Avatar, Badge, Btn, Card, PageHead, Seg, StatTile, Skeleton, InvoiceList } from "../components/kit";
import { StatusDot } from "../components/StatusDot";
import { GrantModal, PlanModal, SuspendModal, ConfirmModal, InviteModal } from "../components/modals";
import type { GodOrg } from "../data/mock";
import { useOrgs, useUsers, useOrgTracks, useOrgConfig, setOrgConfig, useOrgCatalog, setOrgCatalogItem, grantCredits, changePlan, setMemberRole, relTime, nameFromEmail, initialsFrom } from "../lib/admin";

const fmtMonth = (ts: number) => (ts ? new Date(ts).toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" }) : "—");

const O_TABS: [string, string, string][] = [
  ["overview", "Overview", "layout-dashboard"], ["members", "Members", "users"], ["billing", "Billing & plan", "credit-card"],
  ["learning", "Shared learning", "route"], ["catalog", "Catalog", "library"], ["settings", "Settings", "settings"],
];

/* Per-org catalog approval (Phase 5) — toggle which live tracks/lessons this
   org's learners can browse + add. Assigned items always remain visible. */
function OrgCatalogTab({ o }: { o: GodOrg }) {
  const data = useOrgCatalog(o.id);
  // Local visibility overlay so toggles reflect instantly (optimistic).
  const [over, setOver] = useState<Record<string, boolean>>({});
  const vis = (key: string, base: boolean) => (key in over ? over[key] : base);
  const toggle = (itemType: "track" | "lesson", id: string, base: boolean) => {
    const key = `${itemType}:${id}`;
    const next = !vis(key, base);
    setOver((m) => ({ ...m, [key]: next }));
    setOrgCatalogItem(o.id, itemType, id, next).catch(() => setOver((m) => ({ ...m, [key]: !next })));
  };
  return (
    <div className="two-col">
      <Card title="Tracks" icon="route" count={data ? data.tracks.length : undefined} pad={false}>
        {data === null ? (
          <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={20} />)}</div>
        ) : data.tracks.length === 0 ? (
          <div className="gt-empty">No live tracks.</div>
        ) : data.tracks.map((t) => {
          const on = vis(`track:${t.id}`, t.visible);
          return (
            <div className="ls-row" key={t.id}>
              <Icon name={t.icon || "route"} size={14} />
              <span className="ls-n">{t.name}</span>
              <span className="ls-state">{t.lessonCount} lessons</span>
              <Btn variant={on ? "ghost" : "live"} size="sm" icon={on ? "eye" : "eye-off"} onClick={() => toggle("track", t.id, t.visible)}>{on ? "Visible" : "Hidden"}</Btn>
            </div>
          );
        })}
      </Card>
      <Card title="Lessons" icon="library" count={data ? data.lessons.length : undefined} pad={false}>
        {data === null ? (
          <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} h={20} />)}</div>
        ) : data.lessons.length === 0 ? (
          <div className="gt-empty">No lessons in the catalog.</div>
        ) : data.lessons.map((l) => {
          const on = vis(`lesson:${l.id}`, l.visible);
          return (
            <div className="ls-row" key={l.id}>
              <span className={`lvl-tag ${l.type}`}>{l.type}</span>
              <span className="ls-n">{l.title}</span>
              <Btn variant={on ? "ghost" : "live"} size="sm" icon={on ? "eye" : "eye-off"} onClick={() => toggle("lesson", l.id, l.visible)}>{on ? "Visible" : "Hidden"}</Btn>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

export function OrgsPage() {
  const [sel, setSel] = useState<GodOrg | null>(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");

  // Live accounts; null until loaded / signed out.
  const live = useOrgs();
  const allOrgs: GodOrg[] | null = live
    ? live.orgs.map((o) => ({
        id: o.id, name: o.name, domain: o.domain, plan: o.tier.charAt(0).toUpperCase() + o.tier.slice(1),
        seats: o.seats, seatLimit: o.seatLimit, mrr: "—", margin: o.marginPct, status: "active",
        owner: o.owner, created: fmtMonth(o.createdAt), spendToday: o.spendTodayUsd, dailyCap: o.dailyCapUsd, sso: o.sso,
      }))
    : null;

  if (sel) return <OrgDetail o={sel} back={() => setSel(null)} />;
  const rows = (allOrgs ?? []).filter(
    (o) =>
      (status === "All" ||
        (status === "Active" && o.status === "active") ||
        (status === "Past due" && o.status === "past_due") ||
        (status === "Comp" && o.status === "comp")) &&
      (q === "" || (o.name + o.domain).toLowerCase().includes(q.toLowerCase()))
  );
  return (
    <div className="page">
      <PageHead title="Organizations" sub="Every account — plans, seats, revenue and health." />
      <div className="filter-bar">
        <div className="search-inline g grow"><Icon name="search" size={15} /><input placeholder="Search org or domain…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <div className="seg g">{["All", "Active", "Past due", "Comp"].map((s) => <button key={s} aria-pressed={status === s} onClick={() => setStatus(s)}>{s}</button>)}</div>
        <Btn variant="ghost" size="sm" icon="download">Export</Btn>
      </div>
      <Card pad={false}>
        <div className="gtable orgs-t">
          <div className="gt-head"><span>Org</span><span>Plan</span><span>Seats</span><span>MRR</span><span>Margin</span><span>Status</span><span></span></div>
          {allOrgs === null
            ? Array.from({ length: 6 }).map((_, i) => (
                <div className="gt-row" key={i} style={{ alignItems: "center" }}>
                  <span className="gt-org"><Skeleton w={26} h={26} r={7} /><Skeleton w={120} h={12} /></span>
                  <span><Skeleton w={44} /></span><span><Skeleton w={30} /></span><span><Skeleton w={40} /></span>
                  <span><Skeleton w={36} /></span><span><Skeleton w={56} /></span><span />
                </div>
              ))
            : rows.map((o) => (
                <div className="gt-row click" key={o.id} onClick={() => setSel(o)}>
                  <span className="gt-org"><span className="org-mark">{o.name[0]}</span><span><b>{o.name}</b><small>{o.domain}</small></span></span>
                  <span><span className={`plan-pill ${o.plan.toLowerCase()}`}>{o.plan}</span></span>
                  <span className="gt-mono">{o.seats}</span>
                  <span className="gt-mono">{o.mrr}</span>
                  <span className="gt-mono">{o.margin == null ? "—" : o.margin + "%"}</span>
                  <span className="gt-status"><StatusDot s={o.status} />{o.status.replace("_", " ")}</span>
                  <span className="gt-go"><Icon name="chevron-right" size={16} /></span>
                </div>
              ))}
          {allOrgs !== null && rows.length === 0 && <div className="gt-empty">No orgs match these filters.</div>}
        </div>
      </Card>
    </div>
  );
}

function OrgSettingsTab({ o }: { o: GodOrg }) {
  const { config, reload } = useOrgConfig(o.id);
  const editNum = (key: "seatLimit" | "dailyCapUsd", label: string, current: number | null) => {
    const raw = window.prompt(`${label} for ${o.name} (blank = use plan/global default)`, current != null ? String(current) : "");
    if (raw === null) return;
    const trimmed = raw.trim();
    const value = trimmed === "" ? null : Number(trimmed);
    if (value !== null && (!Number.isFinite(value) || value < 0)) return;
    setOrgConfig(o.id, key, value).then(reload).catch(() => {});
  };
  const setRetention = (value: string) => setOrgConfig(o.id, "retention", value === "default" ? null : value).then(reload).catch(() => {});

  const effSeat = config?.seatLimit ?? config?.planSeats ?? null;
  const seatOverridden = config?.seatLimit != null;
  const capOverridden = config?.dailyCapUsd != null;
  const retention = config?.retention ?? "default";
  return (
    <Card title="Org settings & security" icon="settings" pad={false}>
      <div className="set-list">
        <div className="set-row">
          <span className="set-ic"><Icon name="key-round" size={17} /></span>
          <div className="set-body"><div className="set-t">Single sign-on (SSO)</div><div className="set-d">SAML / OIDC via WorkOS</div></div>
          {o.sso ? <Badge tone="live" icon="check">WorkOS-linked</Badge> : <Badge tone="amber">Coming soon</Badge>}
        </div>
        <div className="set-row">
          <span className="set-ic"><Icon name="globe" size={17} /></span>
          <div className="set-body"><div className="set-t">Verified domain</div><div className="set-d">{o.domain}</div></div>
          {o.domain && o.domain !== "—" ? <Badge tone="live" icon="check">Verified</Badge> : <span className="muted-sm">—</span>}
        </div>
        <div className="set-row">
          <span className="set-ic"><Icon name="users" size={17} /></span>
          <div className="set-body"><div className="set-t">Seat limit</div><div className="set-d">{config ? `${config.memberCount} in use${seatOverridden ? " · org override" : " · plan default"}` : "Max members"}</div></div>
          <div className="budget-val">{config === null ? <Skeleton w={28} h={14} /> : effSeat == null ? "∞" : effSeat} <button className="icon-mini" title="Edit seat limit" onClick={() => editNum("seatLimit", "Seat limit", config?.seatLimit ?? null)}><Icon name="pencil" size={13} /></button></div>
        </div>
        <div className="set-row">
          <span className="set-ic"><Icon name="gauge" size={17} /></span>
          <div className="set-body"><div className="set-t">Daily spend cap</div><div className="set-d">{capOverridden ? "Org override (USD)" : "Using global default"}</div></div>
          <div className="budget-val">{config === null ? <Skeleton w={28} h={14} /> : config.dailyCapUsd == null ? "default" : "$" + config.dailyCapUsd} <button className="icon-mini" title="Edit daily cap" onClick={() => editNum("dailyCapUsd", "Daily spend cap (USD)", config?.dailyCapUsd ?? null)}><Icon name="pencil" size={13} /></button></div>
        </div>
        <div className="set-row">
          <span className="set-ic"><Icon name="file-lock-2" size={17} /></span>
          <div className="set-body"><div className="set-t">Data retention</div><div className="set-d">{retention === "none" ? "No transcripts stored (forced off org-wide)" : "Per-user setting (default)"}</div></div>
          <Seg options={["default", "none"]} value={retention} onChange={setRetention} />
        </div>
      </div>
    </Card>
  );
}

function OrgDetail({ o, back }: { o: GodOrg; back: () => void }) {
  const [tab, setTab] = useState("overview");
  const [modal, setModal] = useState<string | null>(null);
  const open = (m: string) => setModal(m);
  // Live members for this org from the cross-org roster; null until loaded.
  const roster = useUsers();
  // Optimistic role overrides so promote/demote reflects instantly (useUsers has
  // no reload). u.role from the roster is the prototype-cased "Admin"/"Member".
  const [roleOverride, setRoleOverride] = useState<Record<string, "admin" | "member">>({});
  const members = roster
    ? roster.users.filter((u) => u.orgId === o.id).map((u) => {
        const name = nameFromEmail(u.email);
        const role = roleOverride[u.id] ?? (u.role.toLowerCase() === "admin" ? "admin" : "member");
        return { id: u.id, name, email: u.email, role, plan: u.plan, turns: u.turns, last: relTime(u.lastTs), status: "active", initials: initialsFrom(name) };
      })
    : null;
  const memberCount = members ? members.length : 0;
  const adminCount = members ? members.filter((m) => m.role === "admin").length : 0;
  const setRole = (userId: string, email: string, role: "admin" | "member") => {
    const prev = roleOverride[userId];
    setRoleOverride((m) => ({ ...m, [userId]: role }));
    setMemberRole(userId, role, email).catch(() => setRoleOverride((m) => {
      const next = { ...m }; if (prev) next[userId] = prev; else delete next[userId]; return next;
    }));
  };
  const orgTracks = useOrgTracks(o.id);
  return (
    <div className="page">
      <div className="detail-head">
        <button className="back-btn" onClick={back}><Icon name="arrow-left" size={16} /></button>
        <span className="org-mark lg">{o.name[0]}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="dh-name">{o.name} {o.status === "past_due" && <Badge tone="amber">Past due</Badge>}{o.status === "comp" && <Badge tone="violet">Comp</Badge>} <span className={`plan-pill ${o.plan.toLowerCase()}`}>{o.plan}</span></div>
          <div className="dh-sub">{o.domain} · owner {o.owner} · since {o.created} · id {o.id}</div>
        </div>
        <div className="dh-actions">
          <Btn variant="ghost" size="sm" icon="arrow-left-right" onClick={() => open("plan")}>Change plan</Btn>
          <Btn variant="ghost" size="sm" icon="crown" onClick={() => open("comp")}>Comp</Btn>
        </div>
      </div>

      <div className="stat-row">
        <StatTile icon="users" value={o.seats} label="Seats used" tone="live" />
        <StatTile icon="banknote" value={o.mrr} label="MRR" />
        <StatTile icon="percent" value={o.margin == null ? "—" : o.margin + "%"} label="Margin" />
        <StatTile icon="flame" value={`$${o.spendToday}`} label="Spend today" />
      </div>

      <div className="tabbar">
        {O_TABS.map(([id, label, ic]) => <button key={id} className={`tab ${tab === id ? "on" : ""}`} onClick={() => setTab(id)}><Icon name={ic} size={15} />{label}</button>)}
      </div>

      {tab === "overview" && (
        <div className="two-col">
          <Card title="Account facts" icon="building-2">
            <div className="kv"><span>Domain</span><b>{o.domain}</b></div>
            <div className="kv"><span>Owner</span><b>{o.owner}</b></div>
            <div className="kv"><span>Admins</span><b>{members === null ? "—" : adminCount === 0 ? "None" : `${adminCount} · ${members.filter((m) => m.role === "admin").map((m) => m.name).join(", ")}`}</b></div>
            <div className="kv"><span>Plan</span><span className={`plan-pill ${o.plan.toLowerCase()}`}>{o.plan}</span></div>
            <div className="kv"><span>Seats</span><b>{o.seats}</b></div>
            <div className="kv"><span>Created</span><b>{o.created}</b></div>
            <div className="kv"><span>SSO</span><b>{o.sso ? "Enabled" : "Off"}</b></div>
          </Card>
          <div className="stack-lg">
            <Card title="Usage caps" icon="gauge">
              <div className="cap-row"><span className="cap-v">${o.spendToday}</span><span className="muted-sm">of {o.dailyCap == null ? "∞" : "$" + o.dailyCap} daily cap</span></div>
              <div className="cap-bar"><i style={{ width: o.dailyCap ? Math.min(100, (o.spendToday / o.dailyCap) * 100) + "%" : "8%" }} /></div>
              <div className="god-actions"><Btn variant="ghost" size="sm" icon="pencil" onClick={() => open("cap")}>Edit cap</Btn></div>
            </Card>
            <Card title="Danger zone" icon="shield-alert" iconColor="var(--g-danger)">
              <div className="danger-list">
                <button className="danger-row" onClick={() => open("suspend")}><Icon name="pause" size={15} />Suspend organization</button>
                <button className="danger-row" onClick={() => open("cancel")}><Icon name="x-circle" size={15} />Cancel subscription</button>
                <button className="danger-row crit" onClick={() => open("delete")}><Icon name="trash-2" size={15} />Delete org &amp; all members</button>
              </div>
            </Card>
          </div>
        </div>
      )}

      {tab === "members" && (
        <Card title="Members" icon="users" count={members ? `${members.length} member${members.length === 1 ? "" : "s"} · ${adminCount} admin${adminCount === 1 ? "" : "s"} · ${o.seats} seats` : undefined}
          action={<Btn variant="live" size="sm" icon="user-plus" onClick={() => open("invite")}>Invite member</Btn>} pad={false}>
          <div className="gtable omem-t">
            <div className="gt-head"><span>Member</span><span>Role</span><span>Plan</span><span>Turns</span><span>Last</span><span></span></div>
            {members === null
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div className="gt-row" key={i} style={{ alignItems: "center" }}>
                    <span className="gt-user"><Skeleton w={28} h={28} r={14} /><Skeleton w={120} h={12} /></span>
                    <span><Skeleton w={50} /></span><span><Skeleton w={44} /></span><span><Skeleton w={30} /></span><span><Skeleton w={40} /></span><span />
                  </div>
                ))
              : members.map((m) => (
                  <div className="gt-row" key={m.id}>
                    <span className="gt-user"><Avatar initials={m.initials} size={28} /><span><b>{m.name}</b><small>{m.email}</small></span></span>
                    <span><span className={`role-pill2 ${m.role}`}>{m.role === "admin" ? <><Icon name="shield-check" size={12} /> Admin</> : "Member"}</span></span>
                    <span><span className={`plan-pill ${m.plan.toLowerCase()}`}>{m.plan}</span></span>
                    <span className="gt-num">{m.turns.toLocaleString()}</span>
                    <span className="gt-mut">{m.status === "suspended" ? <Badge tone="danger">Suspended</Badge> : m.last}</span>
                    <span className="gt-go">
                      {m.role === "admin"
                        ? <Btn variant="ghost" size="sm" icon="user-minus" onClick={() => setRole(m.id, m.email, "member")}>Revoke admin</Btn>
                        : <Btn variant="ghost" size="sm" icon="shield-check" onClick={() => setRole(m.id, m.email, "admin")}>Make admin</Btn>}
                    </span>
                  </div>
                ))}
            {members !== null && members.length === 0 && <div className="gt-empty">No members in this org yet.</div>}
          </div>
        </Card>
      )}

      {tab === "billing" && (
        <div className="two-col">
          <Card title="Subscription" icon="credit-card">
            <div className="kv"><span>Plan</span><span className={`plan-pill ${o.plan.toLowerCase()}`}>{o.plan}</span></div>
            <div className="kv"><span>MRR</span><b>{o.mrr}</b></div>
            <div className="kv"><span>Seats</span><b>{o.seats}</b></div>
            <div className="kv"><span>Status</span><span className="gt-status"><StatusDot s={o.status} />{o.status.replace("_", " ")}</span></div>
            <div className="god-actions">
              <Btn variant="ghost" size="sm" icon="arrow-left-right" onClick={() => open("plan")}>Change plan</Btn>
              <Btn variant="ghost" size="sm" icon="zap" onClick={() => open("grant")}>Grant credits</Btn>
              {o.status === "past_due" && <Btn variant="ghost" size="sm" icon="send">Send dunning</Btn>}
            </div>
          </Card>
          <Card title="Invoices" icon="receipt" pad={false}>
            <InvoiceList query={{ org: o.id }} />
          </Card>
        </div>
      )}

      {tab === "learning" && (
        <Card title="Shared tracks & goals" icon="route" iconColor="var(--g-green)" count={orgTracks ? `${orgTracks.tracks.length} assigned` : undefined} pad={false}>
          {orgTracks === null ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16 }}>{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} h={26} />)}</div>
          ) : orgTracks.tracks.length === 0 ? (
            <div className="gt-empty">No tracks assigned to this org yet. Assign one from Curriculum or the Team page.</div>
          ) : (
            <div className="shared-g">
              {orgTracks.tracks.map((t) => (
                <div className="sharedg-row" key={t.id}>
                  <span className="sharedg-ic"><Icon name={t.icon || "route"} size={15} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="mcp-t">{t.name}</div>
                    <div className="muted-sm">{t.lessonCount} lessons · {t.members} members</div>
                  </div>
                  {t.completionPct != null ? (
                    <><div className="org-meter"><i style={{ width: t.completionPct + "%" }} /></div><span className="gt-mono">{t.completionPct}%</span></>
                  ) : (
                    <span className="muted-sm">—</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "catalog" && <OrgCatalogTab o={o} />}

      {tab === "settings" && <OrgSettingsTab o={o} />}

      {modal === "plan" && <PlanModal target={o.name} current={o.plan} onClose={() => setModal(null)} submit={({ plan, reason, mode, netTermsDays }) => changePlan(o.id, plan, reason, o.name, mode, netTermsDays)} />}
      {modal === "comp" && <PlanModal target={o.name} current={o.plan} preset="Team" presetMode="comp" onClose={() => setModal(null)} submit={({ plan, reason, mode, netTermsDays }) => changePlan(o.id, plan, reason, o.name, mode, netTermsDays)} />}
      {modal === "grant" && <GrantModal target={o.name} onClose={() => setModal(null)} onConfirm={(amount, reason) => grantCredits(o.id, amount, reason, o.name).catch(() => {})} />}
      {modal === "suspend" && <SuspendModal target={o.name} onClose={() => setModal(null)} />}
      {modal === "cap" && <ConfirmModal title="Edit daily spend cap" icon="gauge" desc={`Set the org-wide daily inference ceiling for ${o.name}. Requests pause when hit.`} confirmLabel="Save cap" onClose={() => setModal(null)} />}
      {modal === "invite" && <InviteModal org={o.name} onClose={() => setModal(null)} />}
      {modal === "removeMember" && <ConfirmModal title="Remove member" icon="user-minus" tone="danger" desc="Remove this member from the org? They lose access to shared tracks and seats." confirmLabel="Remove member" onClose={() => setModal(null)} />}
      {modal === "cancel" && <ConfirmModal title="Cancel subscription" icon="x-circle" tone="danger" desc={`${o.name} will drop to Free at the end of the current period. Seats over the Free limit are deactivated.`} confirmLabel="Cancel subscription" onClose={() => setModal(null)} />}
      {modal === "delete" && <ConfirmModal title="Delete organization" icon="trash-2" tone="danger" desc={`Permanently delete ${o.name} and remove all ${memberCount} members. Irreversible.`} confirmLabel="Delete org" onClose={() => setModal(null)} />}
    </div>
  );
}
